import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import { makeStyles } from  "tss-react/mui-compat"
import ScheduleMeetingDialog from "components/meetings/ScheduleMeetingDialog"
import useAuthRedirect from "hooks/useAuthRedirect"
import { useBoolean } from "hooks/useBoolean"
import useCurrent from "lib/use-current"
import { useStateMachine } from "little-state-machine"
import ChatAvailable from "models/convention-api/v1/ChatAvailable"
import React from "react"
import { updateChatStore } from "stores/chat-store"
import {pxToRem} from "../utils/helpers";

const useStyles = makeStyles()(
  (theme) => ({
    container: {
      display: 'flex',
      gap: pxToRem(16),
      flexDirection: 'row',
      flexWrap: 'wrap',
      width: '100%',
      [theme.breakpoints.up(900)]: {
        maxWidth: pxToRem(200),
      },
      [theme.breakpoints.up(1282)]: {
        maxWidth: pxToRem(350)
      }

    },
    button: {
      width: "100%",
      padding: "9px 10px",
      margin: 0,
      whiteSpace: 'nowrap',
      maxWidth: 'initial',
    },
    curatorButton: {
      width: "100%",
      height: "35px",
      fontSize: "14px",
      fontWeight: 700,
      borderRadius: "10px",
    },
    chatMessage: {
      fontStyle: "italic",
      color: "#262B2D",
      fontFamily: "Helvetica Neue",
      fontSize: "15px",
      letterSpacing: 0,
      lineHeight: "20px",
    },
    buttonRoot: {
      width: "100%",
      padding: "9px 10px",
    },
  })
)

interface IProfileSidebar {
  exhibitorName: string
  institutionId: string
  curator?: boolean
  chatAvailable: ChatAvailable["status"]
}

const ProfileSidebar = (props: IProfileSidebar) => {
  const { exhibitorName, institutionId, curator, chatAvailable } = props
  const { classes } = useStyles()

  const authRedirect = useAuthRedirect()
  const current = useCurrent()
  const meetingsEnabled = !!current?.config?.meetings_enabled

  const { actions } = useStateMachine({ updateChatStore })
  const updateChatState = actions.updateChatStore

  const isMeetingDialogOpen = useBoolean(false)
  const enabledChat = (current?.config?.has_exhibitor_chat ?? false) && chatAvailable !== "off"
  const handleOpenChat = () => {
    authRedirect(() => {
      updateChatState({
        institutionId,
      })
    })
  }

  const handleOpenMeetingDialog = () => {
    if (!meetingsEnabled) {
      console.error(`You're trying to open ScheduleMeetingDialog when meetings_enabled is disabled`)
      return
    }

    authRedirect(() => {
      isMeetingDialogOpen.on()
    })
  }

  return (
    <div className={classes.container}>
      {enabledChat && chatAvailable === "away" && (
        <Typography className={classes.chatMessage} align="center" paragraph>
          We are currently not available to chat, please leave a message.
        </Typography>
      )}
      {enabledChat && (
        <Button
          variant="contained"
          color="primary"
          size="large"
          classes={{ root: curator ? classes.curatorButton : classes.button }}
          onClick={handleOpenChat}>
          {chatAvailable === "on" ? "Start Chat" : "Leave a Message"}
        </Button>
      )}
      {meetingsEnabled && (
        <>
          <Button
            variant="outlined"
            color={curator ? "inherit" : "primary"}
            size="large"
            classes={{ root: curator ? classes.curatorButton : classes.buttonRoot }}
            onClick={handleOpenMeetingDialog}>
            Schedule a Meeting
          </Button>
          {isMeetingDialogOpen.value && (
            <ScheduleMeetingDialog
              institutionId={institutionId}
              onClose={isMeetingDialogOpen.off}
              institutionName={exhibitorName}
            />
          )}
        </>
      )}
    </div>
  )
}

export default ProfileSidebar
